import dayjs from 'dayjs';
import {useSelector} from 'react-redux';
import {Edit, NotInterested, Restore} from '@mui/icons-material';
import React, {useCallback, useMemo, useState} from 'react';
import {Box, IconButton, Stack, Typography} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {useLocation} from 'react-router-dom';
import {DataGridCustPagination} from '../../../components/utlis/DataGrid/Pagination';
import {useStylesed} from '../../../components/styles/commonStyled';
import {companyIdSelector} from '../../../store/Slices/companySlice';
import {UserCanAny} from '../../../components/UserCan';
import {useUserCanAny} from '../../../hooks/useUserCan';
import OnDemandActiveDialog from './OnDemandActiveDialog';
import {IDataGridSearchSort} from '../../../dto/Html.dto';
import {
  onDemandGridApiPath,
  useRqGetOnDemandMessageList,
} from '../../../react_query/messages/message-requests/OndemandRequestList';
import {GridCellExpand} from '../../../components/utlis/DataGrid/ExpandCellRender';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import GridFilterHeader from '../../../components/header/GridFilterHeader';
import CustomNoRowsOverlay from '../../../components/utlis/DataGrid/CustomNoRowsOverlay';
import IconAdd from '../../../asset/icons/icon_messaging.svg';
import IconAddEvent from '../../../asset/icons/icon_addsong.svg';
import CustomModal from '../../../components/modal/CustomModal';
import ViewOnDemandMgsList from './ViewOnDemandMgsList';
import {IEsmStatus} from '../../../dto/EventSpecificMessageRequest.dto';
import IconViewButton from '../../../components/Button/IconViewButton';
import DeleteRow from '../../../components/utlis/DataGrid/DeleteRow';
import OnDemandMsgCreate from './OnDemandCreate';
import DownloadButton from '../../../components/Button/DownloadButton';

function renderExpandContent(value: string, width: number) {
  return <GridCellExpand value={value} width={width} />;
}

const OndemandMsgDataGrid = () => {
  const classes = useStylesed();
  const location = useLocation();
  const companyId = useSelector(companyIdSelector);
  const [status, setStatus] = useState<IEsmStatus>('Active');
  const [query, setQuery] = useState<IDataGridSearchSort>({
    search: '',
    page: 0,
    pageSize: 10,
    status: 'Active',
  });
  const canView = useUserCanAny(
    'is-admin,partner,on-demand-message:view,on-demand-message:delete,on-demand-message:update-status,update-on-demand-message',
  );

  const {data, isLoading} = useRqGetOnDemandMessageList(companyId, query);
  const [modalOpen, setModalOpen] = useState(
    Boolean(location.state?.quick_launch_add),
  );

  const handleStatus = (newValue: IEsmStatus) => {
    setStatus(newValue);
    setQuery(prev => {
      return {
        ...prev,
        status: newValue,
      };
    });
  };

  const handleActionButtonClick = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSearch = useCallback((value: string) => {
    setQuery(prev => ({...prev, search: value}));
  }, []);

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'preview',
          type: 'actions',
          headerName: 'Play',
          width: 80,
          flex: 0.5,
          renderCell: (params: GridRenderCellParams) =>
            params.row?.message?.media_track ? (
              <Stack direction='row' spacing={2}>
                <MusicPreviewBtn
                  id={params.row.id}
                  url={params.row.message.media_track?.url}
                  track_name={
                    params.row.message?.media_track?.track_name ||
                    params.row.name
                  }
                  track_artist={
                    params.row.message?.media_track?.track_artist ||
                    params.row.name
                  }
                />
                <DownloadButton
                  filename={params.row.message.media_track?.track_name}
                  url={params.row.message.media_track?.url}
                />
              </Stack>
            ) : null,
        },
        {
          field: 'name',
          headerName: 'Message Name',
          hideable: false,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => {
            return renderExpandContent(
              params.row.name,
              params.colDef.computedWidth,
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created Date',
          hideable: false,
          width: 178,
          renderCell: (params: GridRenderCellParams) => {
            return dayjs(params.value).format('MM/DD/YYYY hh:mm:ss A');
          },
        },
        {
          field: 'created_by',
          headerName: 'Created By',
          hideable: false,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => {
            return renderExpandContent(
              params.row.created_by?.name,
              params.colDef.computedWidth,
            );
          },
        },
        {
          field: 'custom_library',
          headerName: 'Single Play Library',
          hideable: false,
          sortable: false,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => {
            const libs = Array.from(
              new Set(params.row.libraries.map((o: {name: string}) => o.name)),
            );
            return renderExpandContent(
              libs.join(', '),
              params.colDef.computedWidth,
            );
          },
        },
        {
          field: 'expire_date',
          headerName: 'End Date',
          hideable: false,
          width: 100,
          renderCell: (params: GridRenderCellParams) => {
            return params.row.is_expire_date_set
              ? dayjs(params.value).format('MM/DD/YYYY')
              : 'NA';
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Action',
          hideable: false,
          sortable: false,
          width: 200,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => {
            return (
              <ActionDetails
                params={params}
                status={status === 'In Progress' ? 'Active' : status}
              />
            );
          },
        },
      ].map(column => ({
        ...column,
        filterable: false,
      })),
    [status],
  );

  const handlePagination = useCallback((paginate: GridPaginationModel) => {
    setQuery(prev => ({...prev, ...paginate}));
  }, []);

  const handleSortModelChange = (model: GridSortModel) => {
    if (model[0]) {
      setQuery(prev => ({...prev, ...model[0]}));
    }
  };

  return (
    <>
      <Box className='GridTableList'>
        <Box className={classes.r_between}>
          <GridFilterHeader
            handleSearch={handleSearch}
            handleStatus={handleStatus}
            handleActionButtonClick={handleActionButtonClick}
            status={status}
            IconAdd={IconAdd}
            query={query}
            eventName='on-demand-message'
          />
        </Box>

        <Box className='h-auto'>
          <DataGrid
            experimentalFeatures={{ariaV7: true}}
            disableColumnMenu
            disableColumnSelector
            className='custom-data-grid cmc_new_grid h-654 text-white'
            rows={data?.data ?? []}
            columns={columns}
            loading={isLoading}
            rowCount={data?.meta?.total ?? 0}
            paginationModel={{
              page: query.page,
              pageSize: query.pageSize,
            }}
            pageSizeOptions={[10, 25, 50]}
            paginationMode='server'
            onPaginationModelChange={handlePagination}
            sortingMode='server'
            onSortModelChange={handleSortModelChange}
            columnVisibilityModel={{
              actions: canView,
            }}
            disableRowSelectionOnClick
            slots={{
              pagination: DataGridCustPagination,
              noRowsOverlay: () => (
                <CustomNoRowsOverlay
                  imageSrc={IconAdd}
                  message='No message available in OD Message.'
                  buttonText='Create New'
                  IconButton={IconAddEvent}
                  onButtonClick={handleActionButtonClick}
                />
              ),
            }}
            slotProps={{
              columnsPanel: {
                disableHideAllButton: true,
                disableShowAllButton: true,
              },
            }}
          />
        </Box>
        {modalOpen && <OnDemandMsgCreate onClose={handleCloseModal} />}
      </Box>
    </>
  );
};

const ActionDetails = ({
  params,
  status,
}: {
  params: GridRenderCellParams;
  status: 'Active' | 'Inactive';
}) => {
  const companyId = useSelector(companyIdSelector);
  const [isDialogOpen, setIsdialogOpen] = React.useState(false);
  const [messageRequestId, setMessageRequestId] = React.useState(0);
  const [viewModalOpen, setViewModalOpen] = React.useState(false);
  const [viewId, setViewId] = React.useState(0);
  const [isEdit, setIsEdit] = useState(false);

  const handleStatus = useCallback((id: number) => {
    setMessageRequestId(id);
    setIsdialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsdialogOpen(false);
  }, []);

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewId(0);
  };

  const viewMessage = (id: number) => {
    setViewModalOpen(true);
    setViewId(id);
  };

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      spacing={2}>
      <Box className='flex items-center' sx={{gap: '8px'}}>
        <UserCanAny scope='is-admin,partner,on-demand-message:view'>
          <IconViewButton onClick={() => viewMessage(params.row.id)} />
        </UserCanAny>
        <UserCanAny scope='is-admin,on-demand-message:delete'>
          <DeleteRow
            dialog_content={
              <>
                <Typography>
                  This action will delete the following On-Demand Request "
                  {params.row.name}"
                </Typography>
                <Typography>
                  NOTE: The On-Demand message once deleted will not be retrieved
                </Typography>
              </>
            }
            refreshQueryKey={onDemandGridApiPath(companyId)}
            url={`v1/company/${companyId}/message/ondemand-message/${params.row.id}`}
          />
        </UserCanAny>
        {params.row.status !== 'In Progress' && (
          <UserCanAny scope='is-admin,update-on-demand-message'>
            <IconButton
              aria-label='edit'
              color='primary'
              title='Edit'
              onClick={() => setIsEdit(true)}>
              <Edit />
            </IconButton>
          </UserCanAny>
        )}
        {params.row.status !== 'In Progress' ? (
          <UserCanAny scope='is-admin,partner,on-demand-message:update-status'>
            {status === 'Inactive' ? (
              <IconButton
                aria-label='restore'
                color='primary'
                title='Restore'
                onClick={() => handleStatus(params.row.id)}>
                <Restore />
              </IconButton>
            ) : (
              <IconButton
                aria-label='restore'
                color='error'
                title='Deactivate'
                onClick={() => handleStatus(params.row.id)}>
                <NotInterested />
              </IconButton>
            )}
          </UserCanAny>
        ) : null}
      </Box>
      {isDialogOpen ? (
        <OnDemandActiveDialog
          status={status}
          handleCancel={handleCancel}
          messageRequestId={messageRequestId}
        />
      ) : null}
      {isEdit ? (
        <OnDemandMsgCreate
          onClose={() => setIsEdit(false)}
          update_id={params.row.id}
        />
      ) : null}
      <CustomModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        title='Message Preview'
        body={<ViewOnDemandMgsList id={viewId} />}
        actionButtonText='Close Modal'
        onActionButtonClick={handleCloseViewModal}
        maxwidth='740'
        customHeight='605'
      />
    </Stack>
  );
};

export default OndemandMsgDataGrid;
